import {useEffect, useMemo, useRef} from 'react';
import {useMap} from '@vis.gl/react-google-maps';
import {FeatureCollection, Point, GeoJsonProperties} from 'geojson';
import {GoogleMapsOverlay} from '@deck.gl/google-maps';
import {HeatmapLayer} from '@deck.gl/aggregation-layers';

type HeatmapProps = {
  geojson: FeatureCollection<Point, GeoJsonProperties>;
  radius: number;
  opacity: number;
};

type HeatmapPoint = {
  position: [number, number];
  weight: number;
};

const Heatmap = ({geojson, radius, opacity}: HeatmapProps) => {
  const map = useMap();
  const overlayRef = useRef<GoogleMapsOverlay | null>(null);

  const heatmapData = useMemo<HeatmapPoint[]>(() => {
    return (geojson?.features ?? []).flatMap(point => {
      if (point?.geometry?.type !== 'Point') return [];
      const [lng, lat] = point.geometry.coordinates;
      const weight = point.properties?.mag ?? 1;
      if (!Number.isFinite(lng) || !Number.isFinite(lat)
        || Math.abs(lng) > 180 || Math.abs(lat) > 90
        || !Number.isFinite(weight) || weight <= 0) return [];

      return [{position: [lng, lat] as [number, number], weight}];
    });
  }, [geojson]);

  useEffect(() => {
    if (!map) return;
    const overlay = new GoogleMapsOverlay({interleaved: false, layers: []});
    overlay.setMap(map);
    overlayRef.current = overlay;

    return () => {
      overlay.finalize();
      overlayRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    overlayRef.current?.setProps({
      layers: heatmapData.length ? [new HeatmapLayer<HeatmapPoint>({
        id: 'report-heatmap',
        data: heatmapData,
        getPosition: point => point.position,
        getWeight: point => point.weight,
        aggregation: 'SUM',
        radiusPixels: radius,
        opacity,
        // Mantém a progressão verde/amarelo/vermelho do mapa de calor anterior.
        colorRange: [
          [0, 255, 0, 0],
          [0, 255, 0, 255],
          [127, 255, 0, 255],
          [255, 255, 0, 255],
          [255, 127, 0, 255],
          [255, 0, 0, 255],
        ],
      })] : [],
    });
  }, [map, heatmapData, radius, opacity]);

  return null;
};

export default Heatmap;
